<script setup lang="ts">
import {
  computed,
  onBeforeMount,
  watch,
} from 'vue';
import {getRandomId, isMultiChoiceSelectionValid, multiChoiceValidationMessage} from '@/ts/utils/pure-functions';
import type {QuestionMultiChoice} from '@/ts/types/dto/interventions-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {ListBoxOption} from '@/ts/types/component/aria-list-box.type';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import BadgePill from '@/vue/atoms/badge-pill.vue';
import FormRequired from '@/vue/atoms/form-required.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import AriaListBox from '@/vue/molecules/aria-list-box.vue';

const props = defineProps<{
  question: QuestionMultiChoice;
  theme?: 'list-question-in-col' | 'list-question-in-row';
}>();

const emit = defineEmits<{
  readyToGoNext: [],
}>();

const model = defineModel<UuidDTO[]>();

const currentTheme = computed(() => props.theme ?? 'list-question-in-row');

const options = computed(() => props.question.multiChoice.options);

const isSingleChoice = computed(() => {
  return props.question.multiChoice.maxOptions === 1;
});

const isValid = computed(() => isMultiChoiceSelectionValid(model.value, props.question.multiChoice));

const validationMessage = computed(() => multiChoiceValidationMessage(props.question.multiChoice));

const questionLabelId = `question-image-answers-${getRandomId()}`;

const listBoxOptions = computed((): ListBoxOption[] => {
  return options.value.map((option): ListBoxOption => {
    return {
      id: option.id as string,
      text: option.text,
    };
  });
});

const modelValue = computed({
  get(): string[] {
    return model.value as string[];
  },
  set(val: string[]): void {
    model.value = val as UuidDTO[];
    if (isSingleChoice.value && val.length > 0) {
      emit('readyToGoNext');
    }
  },
});

onBeforeMount(() => {
  if (!model.value || model.value.length === 0) {
    model.value = props.question.multiChoiceSelectedIds || [];
  }
});

watch(() => props.question.contentId, () => {
  if (!model.value) {
    model.value = props.question.multiChoiceSelectedIds || [];
  }
});
</script>

<template>
  <div :class="currentTheme">
    <badge-pill theme="blue" :label="$t('question')"/>
    <div :id="questionLabelId" v-safe-html="question.name" class="question"/>

    <div v-if="question.description" v-safe-html="question.description" class="description"/>

    <div class="answer">
      <aria-list-box
        v-model="modelValue"
        :options="listBoxOptions"
        :labeled-by="questionLabelId"
        :disable-default-focus-visible="false"
        :orientation="(currentTheme === 'list-question-in-row') ? 'horizontal' : 'ambiguous'"
        :maximum-selectable="question.multiChoice.maxOptions"
        class="list-box list-box--images"
      >
        <template #item="templateProps">
          <div
            class="image-option-container"
            :class="{'selected': templateProps.isSelected}"
          >
            <lazy-image
              v-if="options.find(opt => opt.id === templateProps.option.id)?.image"
              :src="options.find(opt => opt.id === templateProps.option.id)!.image!"
              :alt-txt="templateProps.option.text"
              :width="384"
              class="option-image"
            />

            <div class="option-text">
              {{ templateProps.option.text }}
            </div>

            <div v-if="templateProps.isSelected" class="selection-indicator">
              <svg-illustration name="check.svg" inner-class="selection-indicator-svg"/>
            </div>
          </div>
        </template>
      </aria-list-box>

      <form-required
        :id="`question-image-answers-${question.contentId}`"
        :valid="isValid"
        :message="$t(validationMessage.key, validationMessage.named)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
// Base styles
.question {
  justify-content: center;
  @include font-display-3;
}

.description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-6);
  text-align: center;
  margin-bottom: var(--Spacing-spacing-3);
}

.answer {
  flex: 1;
}

// Grid layout for image options
.list-box--images {
  display: grid;
  grid-template-columns: repeat(auto-fill, 300px);
  gap: var(--Spacing-spacing-3);
  padding: var(--Spacing-spacing-2) 0;
  justify-content: center;

  @include screen-lg {
    grid-template-columns: repeat(auto-fill, 345px);
  }
}

// Theme layouts - consolidated with shared properties
.list-question-in-col,

.list-question-in-row {
  display: flex;
  justify-content: center;
  flex-direction: column;
  
  .question {
    display: flex;
    align-items: center;
    justify-self: center;
    margin: var(--Spacing-spacing-2) 0;
  }
  .list-box--images {
    justify-content: center; // Override center alignment for row layout
  }
}

.list-question-in-col {
  align-items: center;
  gap: var(--Spacing-spacing-3);

  .question {
    max-width: 800px;
  }

  .answer {
    min-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    @include screen-sm {
      min-width: 400px;
    }
  }
}

// Image option styling
.image-option-container {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: var(--Spacing-spacing-5);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--Neutral-UI-neutral-ui-100);
  background: var(--Neutral-UI-neutral-ui-0);
  min-height: 146px;

  @include screen-lg {
    min-height: 266px;
  }

  &:hover {
    background-color: var(--Neutral-UI-neutral-ui-20);
  }

  &.selected {
    background: var(--Secondary-secondary-color-1);
  }
}

.option-image {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.option-text {
  @include font-body-bold;
  justify-content: center;
  text-align: center;
  padding-top: var(--Spacing-spacing-3) ;
}

.selection-indicator {
  position: absolute;
  top: var(--Spacing-spacing-5);
  right: var(--Spacing-spacing-5);
  z-index: 1;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background-color: var(--Light-Scheme-Neutral-UI-neutral-ui-0);
  display: flex;
  align-items: center;
  justify-content: center;
}

.selection-indicator-svg {
  width: 18px;
  height: 18px;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
}
</style>
